import type { ReactNode } from 'react';
import { Button } from './Button';

/**
 * EmptyState — 空状態 (icon + title + description + optional CTA、Sprint BG-DataSettings 21)。
 *
 * 旧 v1 では PostsList / SearchResult / Admin 各一覧で「該当なし」を inline 直書きしていた
 * (例: `<p>投稿がまだありません</p>` のみ)。v2 では shared-ui で集約し、icon + CTA で
 * 体験を統一する (docs/16 UI patterns 補完)。
 *
 * a11y:
 *   - role="status" + aria-live="polite" — フィルタ変更で件数 0 になった時に SR 通知
 *   - icon は aria-hidden (装飾)
 *
 * 呼出例:
 *   <EmptyState
 *     icon="📭"
 *     title="投稿がまだありません"
 *     description="この地区での最初の投稿を作成しましょう。"
 *     action={{ label: '投稿を作成', onClick: () => navigate('/post') }}
 *   />
 */

export interface EmptyStateProps {
  /** title 上に表示する装飾 icon (絵文字 / SVG element)。default なし。 */
  icon?: ReactNode;
  /** 必須: 主タイトル (例「投稿がまだありません」)。 */
  title: ReactNode;
  /** 任意: 補足テキスト (例「最初の投稿を作成しましょう」)。 */
  description?: ReactNode;
  /** 任意: CTA button (例 「投稿を作成」)。 */
  action?: {
    label: ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
    disabled?: boolean;
  };
  /** padding 抑制用 (Card 内で使う時など)。default 'lg' (py-10 px-6)。 */
  padding?: 'sm' | 'md' | 'lg';
  className?: string;
}

const PADDING_CLASS: Record<NonNullable<EmptyStateProps['padding']>, string> = {
  sm: 'py-4 px-3',
  md: 'py-6 px-4',
  lg: 'py-10 px-6',
};

export function EmptyState({
  icon,
  title,
  description,
  action,
  padding = 'lg',
  className = '',
}: EmptyStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="empty-state"
      className={`flex flex-col items-center text-center ${PADDING_CLASS[padding]} ${className}`.trim()}
    >
      {icon ? (
        <div
          aria-hidden="true"
          data-testid="empty-state-icon"
          className="text-4xl mb-3 leading-none"
        >
          {icon}
        </div>
      ) : null}
      <p className="text-base font-medium text-slate-900">{title}</p>
      {description ? <p className="mt-1 text-sm text-slate-600 max-w-md">{description}</p> : null}
      {action ? (
        <div className="mt-4">
          <Button
            variant={action.variant ?? 'primary'}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
